import PropTypes from "prop-types";

/**
 * RealisticValve – High-fidelity 3D industrial control valve component.
 * Features:
 * - 3D shaded valve body with flange connectors
 * - Animated rotating actuator handwheel / spindle
 * - Open (Green) / Closed (Red) fluid status core
 */
const RealisticValve = ({
  isRunning,
  isOffline,
  statusColor,
  opacity = 1,
  width = "100%",
  height = "100%",
}) => {
  const dotColor = statusColor === "green" ? "#22c55e" : statusColor === "red" ? "#ef4444" : "#94a3b8";

  return (
    <div
      className={`scada-realistic-container ${isOffline ? "scada-realistic--offline" : ""}`}
      style={{
        width,
        height,
        opacity,
      }}
    >
      <svg
        className="scada-realistic-img"
        viewBox="0 0 100 80"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{
          filter: isOffline ? "grayscale(100%) opacity(0.6)" : "drop-shadow(0 4px 10px rgba(0,0,0,0.25))",
        }}
      >
        <defs>
          <linearGradient id="valvBodyGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#94a3b8" />
            <stop offset="50%" stopColor="#cbd5e1" />
            <stop offset="100%" stopColor="#64748b" />
          </linearGradient>
          <linearGradient id="valvWheelGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#f59e0b" />
            <stop offset="100%" stopColor="#b45309" />
          </linearGradient>
          <filter id="valvLedGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
        </defs>

        {/* ── Left Flange ── */}
        <rect x="8" y="32" width="10" height="24" rx="2" fill="url(#valvBodyGrad)" stroke="#475569" strokeWidth="1.5" />
        {/* ── Right Flange ── */}
        <rect x="82" y="32" width="10" height="24" rx="2" fill="url(#valvBodyGrad)" stroke="#475569" strokeWidth="1.5" />

        {/* ── Valve Main Body Cones ── */}
        <polygon points="18,32 50,44 18,56" fill="url(#valvBodyGrad)" stroke="#334155" strokeWidth="1.5" />
        <polygon points="82,32 50,44 82,56" fill="url(#valvBodyGrad)" stroke="#334155" strokeWidth="1.5" />

        {/* ── Center Bonnet Neck ── */}
        <rect x="44" y="20" width="12" height="24" rx="1.5" fill="url(#valvBodyGrad)" stroke="#334155" strokeWidth="1.5" />

        {/* ── Actuator Spindle / Handwheel (Rotates on flow) ── */}
        <g
          style={{
            transformOrigin: "50px 14px",
            animation: isRunning ? "scada-spin 3s linear infinite" : "none",
          }}
        >
          <ellipse cx="50" cy="14" rx="18" ry="6" fill="url(#valvWheelGrad)" stroke="#78350f" strokeWidth="1.5" />
          <circle cx="50" cy="14" r="3" fill="#451a03" />
        </g>

        {/* ── Center Fluid Flow Status Core ── */}
        <circle cx="50" cy="44" r="6" fill={dotColor} filter={isRunning ? "url(#valvLedGlow)" : "none"} />
      </svg>
    </div>
  );
};

RealisticValve.propTypes = {
  liveValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  unit: PropTypes.string,
  isRunning: PropTypes.bool,
  isOffline: PropTypes.bool,
  statusColor: PropTypes.string,
  opacity: PropTypes.number,
  width: PropTypes.number,
  height: PropTypes.number,
};

export default RealisticValve;
